// Labels and small rules of the project meetings tab (docs/tasks/26-project-meetings.md). The server
// (zofino-strapi src/projects/meeting-rules.ts) owns the rules; these mirror them for display.

export const MEETING_STATUS = {
    scheduled: {label: "در پیش", tone: "blue", icon: "event_upcoming"},
    held: {label: "برگزار شده", tone: "green", icon: "event_available"},
    cancelled: {label: "لغو شده", tone: "gray", icon: "event_busy"},
    not_held: {label: "برگزار نشده", tone: "amber", icon: "event_repeat"},
};

export const DECISION_STATUS = {
    open: {label: "باز", tone: "blue", icon: "radio_button_unchecked"},
    in_progress: {label: "در حال اجرا", tone: "amber", icon: "autorenew"},
    done: {label: "اجرا شده", tone: "green", icon: "task_alt"},
    blocked: {label: "مسدود", tone: "red", icon: "block"},
    cancelled: {label: "لغو شده", tone: "gray", icon: "cancel"},
};

export const TASK_STATUS = {
    todo: {label: "شروع نشده", tone: "gray"},
    doing: {label: "در حال انجام", tone: "blue"},
    blocked: {label: "مسدود", tone: "red"},
    done: {label: "تکمیل شده", tone: "green"},
};

const FA = "۰۱۲۳۴۵۶۷۸۹";
export const faDigits = (value) => String(value ?? "").replace(/\d/g, (digit) => FA[Number(digit)]);
export const faNum = (value) => Number(value || 0).toLocaleString("fa-IR");

const noon = (value) => new Date(`${String(value).slice(0, 10)}T12:00:00`);
const format = (value, options) => {
    if (!value) return "—";
    const date = noon(value);
    return Number.isNaN(date.getTime()) ? "—" : new Intl.DateTimeFormat("fa-IR-u-ca-persian", options).format(date);
};
export const faDate = (value) => format(value, {day: "numeric", month: "long", year: "numeric"});
export const faShortDate = (value) => format(value, {day: "numeric", month: "short"});
export const faMonth = (value) => format(value, {month: "short"});
export const faDay = (value) => format(value, {day: "numeric"});
export const faWeekday = (value) => format(value, {weekday: "long"});

// "YYYY-MM-DD" of a local date (the date pickers give Date objects)
export const isoDay = (date) => {
    if (!date) return "";
    const value = date instanceof Date ? date : new Date(date);
    if (Number.isNaN(value.getTime())) return "";
    return `${value.getFullYear()}-${String(value.getMonth() + 1).padStart(2, "0")}-${String(value.getDate()).padStart(2, "0")}`;
};
export const dateOf = (value) => (value ? noon(value) : null);
export const addDays = (value, days) => {
    const date = noon(value);
    date.setDate(date.getDate() + days);
    return isoDay(date);
};

// whole days from today; no date = 999 (never near or late)
export function daysFromToday(value, today) {
    if (!value) return 999;
    const day = String(value).slice(0, 10);
    return Math.round((Date.parse(`${day}T00:00:00Z`) - Date.parse(`${today}T00:00:00Z`)) / 86400000);
}

export function dueLabel(value, today) {
    if (!value) return {text: "بدون موعد", tone: "gray"};
    const days = daysFromToday(value, today);
    if (days < 0) return {text: `${faNum(Math.abs(days))} روز از موعد`, tone: "red"};
    if (days === 0) return {text: "امروز", tone: "amber"};
    if (days <= 3) return {text: `${faNum(days)} روز دیگر`, tone: "amber"};
    return {text: faShortDate(value), tone: "gray"};
}

export function isActionable(decision, today) {
    if (decision.status === "cancelled") return false;
    if (decision.status === "blocked") return true;
    if (decision.status === "done") return !decision.resultVerified;
    return daysFromToday(decision.dueDate, today) <= 3;
}

export function actionPriority(decision, today) {
    if (decision.status === "blocked") return -2000;
    if (decision.status === "done" && !decision.resultVerified) return -1000;
    return daysFromToday(decision.dueDate, today);
}

export const isUnresolved = (decision) => decision.status !== "cancelled" && !(decision.status === "done" && decision.resultVerified);

export const topicKey = (value) => String(value ?? "").trim().toLocaleLowerCase()
    .replace(/ي/g, "ی").replace(/ك/g, "ک").replace(/\s+/g, " ");

export function repeatedTopics(meetings, currentAgenda = []) {
    const counts = new Map();
    meetings.filter((meeting) => meeting.status === "held").forEach((meeting) => {
        new Set((meeting.agenda || []).map(topicKey)).forEach((key) => {
            if (!key) return;
            const entry = counts.get(key) || {topic: meeting.agenda.find((item) => topicKey(item) === key), count: 0};
            entry.count += 1;
            counts.set(key, entry);
        });
    });
    const queued = new Set(currentAgenda.map(topicKey));
    return [...counts.entries()].filter(([key, entry]) => entry.count > 1 && !queued.has(key)).map(([, entry]) => entry).sort((a, b) => b.count - a.count);
}

export function attendance(meetings, key) {
    const held = meetings.filter((meeting) => meeting.status === "held");
    const invited = held.filter((meeting) => meeting.attendees.includes(key)).length;
    const attended = held.filter((meeting) => meeting.present.includes(key)).length;
    return {invited, attended, rate: invited ? Math.round((attended / invited) * 100) : null};
}

// the share of a decision's tasks that are done (project tasks have no percent of their own)
export const doneShare = (tasks) => (tasks.length ? Math.round((tasks.filter((task) => task.status === "done").length / tasks.length) * 100) : 0);

export const sameTitle = (left, right) => topicKey(left) !== "" && topicKey(left) === topicKey(right);

export const plainOf = (html) => String(html || "").replace(/<[^>]*>/g, " ").replace(/&nbsp;/g, " ").replace(/\s+/g, " ").trim();

// readable messages for the server's error keys
export const ERRORS = {
    "project-meeting.error.title-required": "عنوان جلسه را بنویسید.",
    "project-meeting.error.date-invalid": "تاریخ معتبر نیست.",
    "project-meeting.error.time-invalid": "ساعت شروع و پایان را وارد کنید.",
    "project-meeting.error.end-before-start": "ساعت پایان باید بعد از ساعت شروع باشد.",
    "project-meeting.error.attendee-required": "برای جلسه دست‌کم یک عضو را دعوت کنید.",
    "project-meeting.error.owner-required": "مسئول جلسه را انتخاب کنید.",
    "project-meeting.error.reason-required": "دلیل را بنویسید.",
    "project-meeting.error.person-not-in-project": "این فرد عضو پروژه نیست.",
    "project-meeting.error.permission-denied": "دسترسی لازم برای این کار را ندارید.",
    "project-meeting.error.task-permission-denied": "دسترسی ساخت یا تغییر کار پروژه را ندارید.",
    "project-meeting.error.not-held": "این بخش فقط برای جلسه‌ی برگزارشده است.",
    "project-meeting.error.task-exists": "کاری با همین عنوان در پروژه هست؛ «پیوند کار موجود» را انتخاب کنید.",
    "project-meeting.error.task-title-required": "عنوان کار را بنویسید.",
    "project-meeting.error.decision-title-required": "عنوان تصمیم را بنویسید.",
    "project-meeting.error.target-invalid": "هدف یا مایلستون انتخاب‌شده معتبر نیست.",
    "project-meeting.error.note-required": "متن پیگیری را بنویسید.",
    "project-meeting.error.meeting-not-found": "این جلسه پیدا نشد.",
    "project-meeting.error.decision-not-found": "این تصمیم پیدا نشد.",
    "project-meeting.error.task-not-found": "این کار پیدا نشد.",
};

export const errorText = (error, fallback = "ذخیره نشد. دوباره تلاش کنید.") => {
    const key = error?.response?.data?.error?.message || error?.message;
    return ERRORS[key] || fallback;
};

// the shared date picker works in Unix seconds; the meetings tab keeps days as "YYYY-MM-DD"
export const daySeconds = (value) => (value ? Math.floor(new Date(`${String(value).slice(0, 10)}T12:00:00`).getTime() / 1000) : null);
export const dayOfSeconds = (seconds) => (seconds ? isoDay(new Date(Number(seconds) * 1000)) : "");

// a meeting place that is a link opens it (http(s)://… or www.…)
export function linkOf(text) {
    const match = String(text || "").match(/(https?:\/\/[^\s]+|www\.[^\s]+)/i);
    if (!match) return null;
    const url = match[1].replace(/[).,،؛]+$/, "");
    return /^https?:\/\//i.test(url) ? url : `https://${url}`;
}

// the workspace's active members, for invitees and the meeting owner
export function workspacePeople(memberships) {
    const people = new Map();
    (memberships || []).forEach((membership) => {
        if (!membership || ["removed", "restricted"].includes(membership.memberShipStatus)) return;
        const key = String(membership.documentId || membership.id || "");
        const member = membership.member || {};
        const name = member.fullname || [member.firstname, member.lastname].filter(Boolean).join(" ") || member.username || "";
        if (key && name && !people.has(key)) people.set(key, {key, name, member});
    });
    return [...people.values()];
}
